import { TrashIcon } from "@heroicons/react/24/solid";
import { loanDetailsData, periodList } from "configs/loanDetails.config";
import { opportunityData } from "configs/opportunity.config";
import { toWordsOrdinal } from "number-to-words";
import { Col, Row } from "react-bootstrap";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { DatePicker, Dropdown, IconButton, Input, Textarea } from "stories";
import { currencyRules, currencyStyle } from "utils/customHooks/currencyFormat";
import { staticEntities } from "../../constants";
const LenderItem = ({ index, onDelete, opportunity }) => {
  const form = useFormContext();
  const [beingPaidOff, dischargeFeeIncurred, interestReserve] = useWatch({
    control: form.control,
    name: [
      `lenders[${index}].being_paid_off`,
      `lenders[${index}].discharge_fee_incurred`,
      `lenders[${index}].interest_reserve`,
    ],
  });
  const [loanStructure, interestRateType] = useWatch({
    control: form.control,
    name: [
      `lenders[${index}].loan_structure`,
      `lenders[${index}].interest_rate_type`,
    ],
  });
  const {
    formState: { errors },
  } = form;
  return (
    <div className={"w-100 border-gray p-4 mt-2"}>
      <Row>
        <Col>
          <div className="section-title fw-bold d-flex justify-content-between w-100">
            <span className="capitalize">
              {toWordsOrdinal(index + 1)} Mortgage
            </span>
            <span>
              <IconButton onClick={onDelete}>
                <TrashIcon className="icon-md text-red" />
              </IconButton>
            </span>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Input
            label={"Name of the Lender"}
            {...form.register(`lenders[${index}].name`)}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Controller
            name={`lenders[${index}].initial_loan_amount`}
            control={form.control}
            rules={currencyRules}
            render={({ field }) => {
              return (
                <Input
                  label={"Initial Loan Amount"}
                  placeholder={"Amount"}
                  type="currency"
                  startAdornment="$"
                  step="0.01"
                  {...field}
                  value={field.value || ""}
                  error={
                    errors?.lenders &&
                    errors?.lenders[index]?.initial_loan_amount?.message
                  }
                />
              );
            }}
          />
        </Col>
        <Col xs={6}>
          <Controller
            name={`lenders[${index}].principle_outstanding`}
            control={form.control}
            rules={currencyRules}
            render={({ field }) => {
              return (
                <Input
                  label={"Principal Outstanding"}
                  placeholder={"Amount"}
                  type="currency"
                  startAdornment="$"
                  step="0.01"
                  {...field}
                  value={field.value || ""}
                  error={
                    errors?.lenders &&
                    errors?.lenders[index]?.principle_outstanding?.message
                  }
                />
              );
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={4}>
          <Dropdown
            label={"Loan Type"}
            fullWidth
            list={opportunityData.ExistingFinancingLoanTypeList}
            {...form.register(`lenders[${index}].loan_type`)}
          />
        </Col>
        <Col xs={4}>
          <Dropdown
            label={"Mortgage Position"}
            fullWidth
            list={opportunityData.financingTypeList}
            {...form.register(`lenders[${index}].mortgage_position`)}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={4}>
          <Dropdown
            label={"Loan Structure"}
            list={loanDetailsData.loanStructure}
            fullWidth
            {...form.register(`lenders[${index}].loan_structure`)}
          />
        </Col>
        {loanStructure === "interest + principal" && (
          <Col xs={4}>
            <Input
              label={"Amortization Period"}
              type="number"
              step="any"
              {...form.register(`lenders[${index}].amortization_period.count`, {
                setValueAs: (v) => Number(parseFloat(v)),
                min: {
                  value: 1,
                  message: "This field should be greater than 0",
                },
              })}
              error={
                errors.lenders &&
                errors.lenders[index]?.amortization_period?.count?.message
              }
              containerClassName="pr-0"
              endDivider
              endAdornment={
                <Dropdown
                  list={periodList}
                  interactivity={false}
                  containerClassName="mb-0"
                  {...form.register(
                    `lenders[${index}].amortization_period.unit`,
                    {
                      onChange: (e) => {
                        if (
                          !form.getValues(`lenders[${index}].loan_term.count`)
                        ) {
                          form.setValue(
                            `lenders[${index}].loan_term.unit`,
                            e.target.value
                          );
                        }
                      },
                    }
                  )}
                />
              }
            />
          </Col>
        )}
      </Row>
      <Row>
        <Col xs={3}>
          <Controller
            name={`lenders[${index}].being_paid_off`}
            control={form.control}
            render={({ field }) => {
              const onChange = (e) => {
                e.target.value = e.target.value === "true";
                field.onChange(e);
              };
              return (
                <Dropdown
                  label={"Is the loan being paid off?"}
                  list={staticEntities.booleanList}
                  fullWidth
                  {...field}
                  onChange={onChange}
                  value={String(field.value)}
                />
              );
            }}
          />
        </Col>
        <Col xs={4}>
          {beingPaidOff && (
            // TODO: this field should be parsed as number, the api has an issue
            <Controller
              name={`lenders[${index}].mortgage_payout_amount`}
              control={form.control}
              rules={currencyRules}
              render={({ field }) => {
                return (
                  <Input
                    label={"Mortgage Payout Amount"}
                    type="currency"
                    startAdornment="$"
                    step="0.01"
                    {...field}
                    value={field.value || ""}
                    error={
                      errors?.lenders &&
                      errors?.lenders[index]?.mortgage_payout_amount?.message
                    }
                  />
                );
              }}
            />
          )}
        </Col>
      </Row>
      {beingPaidOff && (
        <Row>
          <Col xs={3}>
            <Controller
              name={`lenders[${index}].discharge_fee_incurred`}
              control={form.control}
              render={({ field }) => {
                const onChange = (e) => {
                  e.target.value = e.target.value === "true";
                  field.onChange(e);
                };
                return (
                  <Dropdown
                    label={"Discharge Fee Incurred?"}
                    list={staticEntities.booleanList}
                    fullWidth
                    {...field}
                    onChange={onChange}
                    value={String(field.value)}
                  />
                );
              }}
            />
          </Col>
          <Col xs={4}>
            {dischargeFeeIncurred && (
              <Controller
                name={`lenders[${index}].discharge_fee`}
                control={form.control}
                rules={currencyRules}
                render={({ field }) => {
                  return (
                    <Input
                      label={"Discharge Fee"}
                      type="currency"
                      startAdornment="$"
                      step="0.01"
                      {...field}
                      value={field.value || ""}
                      error={
                        errors?.lenders &&
                        errors?.lenders[index]?.discharge_fee?.message
                      }
                    />
                  );
                }}
              />
            )}
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={4}>
          <Dropdown
            label={"Payment Frequency"}
            list={loanDetailsData.paymentFrequencyList}
            fullWidth
            name={"payment_frequency"}
            {...form.register(`lenders[${index}].payment_frequency`)}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={4}>
          <Input
            label={"Loan Term"}
            name={"loan_term"}
            type="number"
            step="any"
            containerClassName="pr-0"
            {...form.register(`lenders[${index}].loan_term.count`, {
              setValueAs: (v) => Number(parseInt(v)),
              min: {
                value: 1,
                message: "This field should be greater than 0",
              },
            })}
            error={
              errors.lenders && errors.lenders[index]?.loan_term?.count?.message
            }
            endDivider
            endAdornment={
              <Dropdown
                list={periodList}
                interactivity={false}
                containerClassName="mb-0"
                {...form.register(`lenders[${index}].loan_term.unit`, {
                  onChange: (e) => {
                    if (
                      !form.getValues(
                        `lenders[${index}].amortization_period.count`
                      )
                    ) {
                      form.setValue(
                        `lenders[${index}].amortization_period.unit`,
                        e.target.value
                      );
                    }
                  },
                })}
              />
            }
          />
        </Col>
      </Row>
      <Row>
        <Col xs={3}>
          <Dropdown
            label={"Interest Rate Type"}
            list={loanDetailsData.interestRate}
            fullWidth
            {...form.register(`lenders[${index}].interest_rate_type`)}
          />
        </Col>
        <Col xs={4}>
          {interestRateType === "variable" ? (
            <Controller
              key="premium"
              control={form.control}
              name={`lenders[${index}].premium`}
              render={({ field }) => {
                return (
                  <Input
                    key="premium"
                    label={"Premium"}
                    type="currency"
                    prefix=""
                    step="any"
                    startAdornment={`Prime (${currencyStyle(
                      opportunity.prime,
                      2
                    )}) +`}
                    endDivider
                    endAdornment={<span className="pl-2">%</span>}
                    {...field}
                  />
                );
              }}
            />
          ) : (
            <Controller
              key="interest_rate"
              control={form.control}
              name={`lenders[${index}].interest_rate`}
              render={({ field }) => {
                return (
                  <Input
                    key="rate"
                    label={"Interest Rate"}
                    type="currency"
                    prefix=""
                    step="any"
                    endDivider
                    endAdornment={<span className="pl-2">%</span>}
                    {...field}
                  />
                );
              }}
            />
          )}
        </Col>
      </Row>
      <Row>
        <Col xs={3}>
          <Controller
            name={`lenders[${index}].interest_reserve`}
            control={form.control}
            render={({ field }) => {
              const onChange = (e) => {
                e.target.value = e.target.value === "true";
                field.onChange(e);
              };
              return (
                <Dropdown
                  label={"Is there an Interest Reserve?"}
                  list={staticEntities.booleanList}
                  fullWidth
                  {...field}
                  onChange={onChange}
                  value={String(field.value)}
                />
              );
            }}
          />
        </Col>
        <Col xs={4}>
          {interestReserve && (
            <Controller
              name={`lenders[${index}].interest_reserve_amount`}
              control={form.control}
              rules={currencyRules}
              render={({ field }) => {
                return (
                  <Input
                    label={"Interest Reserve Amount"}
                    type="currency"
                    startAdornment="$"
                    step="0.01"
                    {...field}
                    value={field.value || ""}
                    error={
                      errors?.lenders &&
                      errors?.lenders[index]?.interest_reserve_amount?.message
                    }
                  />
                );
              }}
            />
          )}
        </Col>
      </Row>
      <Row>
        <Col xs={4}>
          <Controller
            control={form.control}
            name={`lenders[${index}].maturity_date`}
            render={({ field }) => {
              return <DatePicker label={"Maturity Date"} {...field} />;
            }}
          />
        </Col>
        <Col xs={4}>
          <Controller
            name={`lenders[${index}].current_debt_service`}
            control={form.control}
            rules={currencyRules}
            render={({ field }) => {
              return (
                <Input
                  label={"Current Debt Service"}
                  type="currency"
                  startAdornment="$"
                  step="0.01"
                  {...field}
                  value={field.value || ""}
                  error={
                    errors?.lenders &&
                    errors?.lenders[index]?.current_debt_service?.message
                  }
                />
              );
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={8}>
          <Textarea
            label={"Additional Notes"}
            {...form.register(`lenders[${index}].additional_notes`)}
          />
        </Col>
      </Row>
    </div>
  );
};

export default LenderItem;
